/**
 * CitationPanel.tsx
 *
 * Phase 2a: Sources tab removed. The map is the only view.
 *
 * [N] inline citations in the answer now pan the map to the corresponding
 * clause node and highlight it, rather than scrolling a sources list.
 * The connection: activeCitationNum → citation.id → mapRef.focusNode(id).
 *
 * Props retained for backward-compatibility with App.tsx; unused ones
 * (defaultView, onViewChange) are silently ignored.
 */

import { useCallback, useEffect, useRef } from 'react';
import type { Citation, RetrievalTree } from '../types';
import ReactFlowRetrievalMap from './ReactFlowRetrievalMap';
import type { ReactFlowRetrievalMapHandle } from './ReactFlowRetrievalMap';

interface Props {
  citations: Citation[];
  activeCitationNum: number | null;
  onCitationClick: (num: number) => void;
  /** The user query that produced these citations */
  queryText?: string;
  /** Retrieval Map tree data */
  retrievalTree?: RetrievalTree | null;
  /** True while the SSE stream is active */
  isStreaming?: boolean;
  /** Legacy: ignored — map is now the only view */
  defaultView?: 'sources' | 'map';
  /** Legacy: ignored */
  onViewChange?: (view: 'sources' | 'map') => void;
}

export default function CitationPanel({
  citations,
  activeCitationNum,
  onCitationClick,
  retrievalTree,
  isStreaming,
}: Props) {
  const mapRef = useRef<ReactFlowRetrievalMapHandle>(null);

  // Keep a stable ref to citations so onClauseSelect doesn't recreate every render.
  // Without this, the inline arrow function passed to ReactFlowRetrievalMap recreates
  // on every render → onClauseSelectStable in ReactFlowRetrievalMap recreates →
  // ctxValueFull (React context) recreates → all clause nodes re-render → cascade.
  const citationsRef = useRef(citations);
  citationsRef.current = citations;

  // Stable callback — never recreates, reads latest citations via ref.
  const handleClauseSelect = useCallback((clauseId: string | null) => {
    if (!clauseId) return;
    const cit = citationsRef.current.find(c => c.id === clauseId);
    if (cit) onCitationClick(cit.number);
  }, [onCitationClick]);

  /**
   * [N] citation → map pan
   *
   * When the active citation number changes (user clicked [N] in the answer),
   * find the corresponding clause id and focus it on the map.
   */
  useEffect(() => {
    if (activeCitationNum === null) return;
    const citation = citationsRef.current.find(c => c.number === activeCitationNum);
    if (!citation) return;
    mapRef.current?.expandNode(citation.id);
  }, [activeCitationNum]); // citations accessed via ref — no dep churn

  return (
    <div className="citation-panel" data-tour="source-panel">
      <ReactFlowRetrievalMap
        ref={mapRef}
        tree={retrievalTree ?? null}
        isStreaming={isStreaming ?? false}
        onClauseSelect={handleClauseSelect}
      />
    </div>
  );
}
